import axios from 'axios';
import { API_CONFIG } from '../config';
import { getPartnerApiToken } from '../auth/AuthContext';
import { notifySessionExpired } from './sessionExpiry';

// ---------------------------------------------------------------------------
// Partner API client
//
// Wraps calls to the partner API (/v1/*) from the Dev Portal browser. The
// Dev Portal authenticates to the *developer* API with a Cognito JWT, but
// the partner API requires an sk_* / st_* / ssk_* bearer. To bridge the two:
//
//   1. AuthContext.getPartnerApiToken(env) mints a short-lived st_* via
//      GET /developer/scoped-token?tenant=<env>&ttl=900 (the Cognito-
//      authenticated developer API).
//   2. This axios instance attaches the minted st_* as the Authorization
//      header on every outgoing /v1/* request.
//
// Per-page env selection: every exported call accepts `env` ('live' | 'test')
// as the first arg. The interceptor reads it from the request config so the
// same client instance can serve both envs concurrently — useful when the
// Keys page is showing one env while a background refetch happens against
// the other.
//
// See:
//   - docs/handoffs/mr-dev-portal-scoped-keys-ui.md § Step 1
//   - PartnerAdminKeyHandler.java for the consumed endpoint contracts
// ---------------------------------------------------------------------------

const partnerApi = axios.create({
  baseURL: API_CONFIG.partnerApiBase,
});

/**
 * Request interceptor: attach a freshly-minted st_* bearer. Exported for
 * direct unit testing of the mint-failure → session-expiry path.
 *
 * Callers pass `env` directly on the request config (a custom property — not
 * part of the axios spec, but axios passes unknown keys through).
 */
export async function attachPartnerToken(config) {
  const env = config.env || 'test';
  // Optional per-request context: callers that operate IN a data context (the
  // scoped-key wizard's AccessProfile + key-mint calls) pass `context` so the
  // minted token is confined to it. Absent → getPartnerApiToken defaults to the
  // base default context (#324 / ADR-0049).
  const context = config.context;
  let token;
  try {
    token = await getPartnerApiToken(env, context);
  } catch (e) {
    // The st_* mint is Cognito-authenticated (a raw fetch, NOT this axios
    // instance — so a mint 401 never reaches the response interceptor below),
    // and an expired/absent session is the common failure, so escalate to a
    // clean re-login. Rethrow either way so the call still rejects.
    //
    // BUT NOT AUTOMATICALLY ON A 404. GET /developer/scoped-token returns 404
    // from SEVEN distinct sites in DeveloperScopedTokenHandler, and they do not
    // share one cause or one remedy:
    //
    //   :744 :759 :764 :817  the sub-user arms — no tenantId, no AccessProfile
    //                        in this context, profile suspended, or (new in
    //                        0.43.0, #877) the USER suspended. Uniform by
    //                        design: "no cross-tenant existence channel."
    //   :653                 "Context not found: <id>" on the OWNER path. NOT
    //                        uniform — it names the context — and it fires when
    //                        the requested context does not exist in-tenant.
    //   :242 :257            the partner record itself did not resolve.
    //                        resolvePartnerUser() is CLAIM-driven
    //                        (`active_partner_user_id`, falling back to
    //                        `custom:uuid` — BaseDeveloperHandler), so a stale
    //                        or missing claim lands here. Re-minting the JWT is
    //                        exactly the fix: the multi-membership variant of
    //                        this same degradation throws
    //                        SessionRefreshRequiredException, whose message is
    //                        literally "Please sign out and back in to refresh."
    //
    // What is common to all seven is that the Cognito session is not
    // necessarily dead, so an AUTOMATIC bounce to /login is wrong — and it
    // LOOPS: the sign-in succeeds, returnTo lands back on the same page, the
    // mint is refused again, and around. The once-per-page `redirecting` guard
    // resets on every full navigation, and LoginPage does not bounce an
    // already-authenticated user, so nothing breaks the cycle.
    //
    // 0.43.0 did not create that loop (the suspended-profile arm dates to the
    // route's 2026-05-28 identity resolution) but it widened the door into it,
    // because suspending a member via PUT /v1/users/{id} now reaches it too and
    // suspending a member is a thing an owner does deliberately.
    //
    // So: stop the automatic redirect and let the call reject normally, where
    // the page's own ApiErrorAlert can show it. The copy must NOT assert a
    // cause — the status alone cannot tell these apart, and matching on the
    // server's message text is the fragile version of this — and it must not
    // DENY re-signing-in either, which an earlier draft of this fix did: that
    // is the correct remedy for the claim-resolution arm above. It names both
    // real remedies and leaves the developer to find which applies.
    if (isMintRefusal(e)) {
      throw new Error(
        'This environment could not be opened for your account. If your access '
        + 'changed recently, signing out and back in may fix it. Otherwise your '
        + 'access may have been suspended, or this app context may no longer '
        + 'exist — ask your account owner to check.',
      );
    }
    notifySessionExpired();
    throw e;
  }
  config.headers.Authorization = `Bearer ${token}`;
  return config;
}

partnerApi.interceptors.request.use(attachPartnerToken);

/**
 * Whether a partner-token mint failure is the developer API REFUSING to mint
 * (a uniform 404 from GET /developer/scoped-token) rather than the caller's
 * Cognito session being gone. Only the former must skip the re-login redirect.
 *
 * Deliberately narrow: 404 is the status that route uses for "I will not mint
 * for you", and every other failure — a 5xx, a network error, a missing idToken
 * (which throws before any fetch and so carries no `status`) — keeps the
 * existing escalate-to-re-login behaviour. Widening this to "any error with a
 * status" would swallow the real expired-session case.
 *
 * NOT widened to 403, though the handler's own 403 (:234, non-owner with no
 * membership) is another case re-login cannot fix. Two reasons, and the first
 * is decisive: this route sits behind the API Gateway Cognito authorizer, and
 * an authorizer denial is not guaranteed to arrive as 401 rather than 403 — so
 * treating 403 as a refusal risks swallowing the genuine expired session this
 * whole mechanism exists to catch, which is the one direction that must not
 * fail. Second, that site is annotated in the handler as likely unreachable.
 * The trade is deliberate: a 403 keeps the (wrong but harmless-in-practice)
 * redirect rather than risking a real session failure going unhandled.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isMintRefusal(err) {
  return err?.status === 404;
}

/**
 * Response error handler: a 401 from the partner API means the bearer (st_*)
 * was rejected — redirect to a clean re-login. Exported for direct unit
 * testing. Always rethrows.
 */
export function handlePartnerApiError(error) {
  if (error?.response?.status === 401) notifySessionExpired();
  return Promise.reject(error);
}

partnerApi.interceptors.response.use((response) => response, handlePartnerApiError);

// FC-01 (#163): the partner-API list endpoints now return a {data, nextCursor} envelope instead of a
// bare top-level array. Unwrap to the data array; tolerate the old bare-array shape too so this stays
// correct across the deploy window (the dev-portal and the API deploy as separate jobs, either order).
const unwrapList = (r) => (Array.isArray(r.data) ? r.data : r.data?.data ?? []);

// Page size + drain ceiling for endpoints that need EVERY row, not just the
// first page — mirrors ui/admin-app/src/lib/drainPages.ts (see that file's
// header comment for the full "why 100, why maxPages+1" reasoning; ported
// here rather than shared because dev-portal has no equivalent lib/ home and
// this app is plain JS, not TS).
const LIST_PAGE_SIZE = 100;
const LIST_MAX_PAGES = 50;

/**
 * Drain every page of a cursor-paginated partner-API list endpoint into one
 * array. `fetchPage(startFrom)` returns one `{data, nextCursor}` page (call
 * with `undefined` for the first page). Termination is on any FALSY
 * `nextCursor` — `null`, `undefined`, or `''` — not strictly `null`/absent; a
 * short or empty page with a genuinely live (truthy) cursor still continues
 * the drain (see drainPages.ts for why a blank-string cursor is folded into
 * the terminal case rather than treated as a real resume position). Fails
 * CLOSED: throws rather than silently returning a partial listing if the
 * drain outruns `maxPages` (the ceiling allows one extra request beyond
 * `maxPages` pages of data, since a full final page still carries a live
 * cursor — see drainPages.ts's rule 2 for why).
 */
async function drainList(fetchPage, maxPages = LIST_MAX_PAGES) {
  const all = [];
  let startFrom;
  for (let page = 0; page <= maxPages; page++) {
    // Guard against a null/undefined body (e.g. a 204) the same way the
    // unwrapList this replaced did — destructuring straight off `fetchPage`'s
    // result would throw instead of degrading to an empty, terminal page.
    const { data, nextCursor } = (await fetchPage(startFrom)) ?? {};
    if (data) all.push(...data);
    if (!nextCursor) return all;
    startFrom = nextCursor;
  }
  throw new Error(
    `drainList: listing still not exhausted after ${maxPages + 1} requests ` +
      `(${all.length} rows read). Refusing to return a partial result.`,
  );
}

// ---------------------------------------------------------------------------
// Scoped keys — /v1/admin/keys/scoped/*
// Backend: PartnerAdminKeyHandler.java
//
// list/get/revoke are CONTEXT-SCOPED (#387 / #442). A scoped key lives in the
// context it was issued in, and PartnerAdminKeyHandler filters reads/revokes by
// the caller token's context (R1-01/#242 keyOutsideCallerContext) — so a token
// confined to one context can't see or revoke a key issued in another. Each call
// takes `context` and passes it to the interceptor, which mints a token PINNED to
// that context (getPartnerApiToken(env, context)). This mirrors the CLI's
// per-context revoke (`vectros key revoke --context`): the server still enforces
// tenant binding on the pinned token, so `context` only selects which of the
// caller's OWN contexts to act in — it never crosses a tenant. No new backend
// route: the existing partner endpoints + the per-context mint do the job.
// ---------------------------------------------------------------------------

export const listScopedKeys = (env, context) =>
  partnerApi.get('/v1/admin/keys/scoped', { env, context }).then(unwrapList);

export const getScopedKey = (env, context, keyId) =>
  partnerApi.get(`/v1/admin/keys/scoped/${keyId}`, { env, context }).then((r) => r.data);

// Binds a key to body.contextId — pass that context so the mint token is
// confined to it (createScopedKey references the context's AccessProfile;
// a token confined to a DIFFERENT context 403s — #324 / ADR-0049).
export const createScopedKey = (env, body) =>
  partnerApi.post('/v1/admin/keys/scoped', body, { env, context: body.contextId }).then((r) => r.data);

export const revokeScopedKey = (env, context, keyId) =>
  partnerApi.delete(`/v1/admin/keys/scoped/${keyId}`, { env, context });

// ---------------------------------------------------------------------------
// (API logs moved off the partner API — see developerApi.getAdminLogs.)
//
// The dev-portal's partner bearer is an st_* pinned to a single context, so
// /v1/admin/logs is context-CONFINED and can only ever return that one
// context's logs — never the tenant-wide, cross-context view an owner needs.
// Tenant-wide logs now ride the owner-gated
// /developer/logs dev-API route (ADR-0060 / #582), mirroring the admin-app's
// #574 re-point. Nothing here calls the partner logs route anymore.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// AccessProfiles — /v1/app-contexts/{ctx}/profiles/*
// Backend: PartnerAppContextHandler.java (MR #1)
// Used by the ScopedKeyCreateDialog's "App context" wizard step.
//
// NOTE: app-context LIST/CREATE is NOT here — it goes through the OWNER-gated
// developer-API route (developerApi.listAppContexts/createAppContext), because
// the dev-portal's partner bearer is confined to a single context and can
// neither enumerate nor create the OWNER's data contexts (#324 / ADR-0049).
// ---------------------------------------------------------------------------

/**
 * Returns the AccessProfile for (contextId, principalId), or null if absent.
 * principalId must be passed in already-prefixed form (e.g. "usr_abc-123").
 * Surfaces a clean null on 404 so the wizard can branch on "needs profile".
 */
// AccessProfile read/create live UNDER an AppContext — pass `context: contextId`
// so the token is minted confined to that data context (these endpoints are
// context-confined and 403 a token minted for a different context — #324 / ADR-0049).
export const getAccessProfile = (env, contextId, principalId) =>
  partnerApi
    .get(`/v1/app-contexts/${contextId}/profiles/${principalId}`, { env, context: contextId })
    .then((r) => r.data)
    .catch((e) => (e.response?.status === 404 ? null : Promise.reject(e)));

export const createAccessProfile = (env, contextId, body) =>
  partnerApi.post(`/v1/app-contexts/${contextId}/profiles`, body, { env, context: contextId }).then((r) => r.data);

// ---------------------------------------------------------------------------
// PartnerUsers — /v1/users
// Backend: PartnerUserHandler.java
// MR #2 added `type` field (HUMAN | SERVICE) used by the "Bind to user"
// wizard step to filter humans vs service principals.
// ---------------------------------------------------------------------------

// Drains every page: the Bind step's Humans/Services picker needs the COMPLETE
// tenant roster, not just the server's default first page (20 rows) — an
// undrained call left any tenant past that count with a picker silently
// missing every user beyond it, no error, no "load more".
export const listUsers = (env) =>
  drainList((startFrom) =>
    partnerApi
      .get('/v1/users', { env, params: { startFrom, limit: LIST_PAGE_SIZE } })
      .then((r) => (Array.isArray(r.data) ? { data: r.data, nextCursor: null } : r.data)),
  );

/**
 * Create a SERVICE-type PartnerUser (a "service principal"). Email-less.
 * Used by the wizard's inline "Create new service principal" action.
 */
export const createServicePrincipal = (env, externalId) =>
  partnerApi
    .post('/v1/users', { externalId, type: 'SERVICE' }, { env })
    .then((r) => r.data);

export default partnerApi;
